import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';

interface RemoveMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    member: any;
    onConfirm: () => void;
}

/**
 * Remove Member Dialog
 * Confirmation dialog for removing a member from the organization
 */
const RemoveMemberDialog: React.FC<RemoveMemberDialogProps> = ({
    open,
    onOpenChange,
    member,
    onConfirm
}) => {
    const { t } = useTranslation();

    if (!member) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                        {t('organization.members.removeMember')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('organization.members.removeMemberConfirm')}
                    </DialogDescription>
                </DialogHeader>

                <Alert variant="destructive">
                    <AlertDescription>
                        This will remove <strong>{member.displayName || member.email}</strong> from your organization.
                        They will lose access to all organization resources.
                    </AlertDescription>
                </Alert>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.buttons.cancel')}
                    </Button>
                    <Button variant="destructive" onClick={onConfirm}>
                        {t('organization.members.removeMember')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RemoveMemberDialog;
